import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import { SectionCard } from '../../components/PageChrome';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';
import { formatMoney } from '../../utils/format';

export default function ProductsPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const productData = await shopApi.listProducts(token, selectedShopId);
      setProducts(productData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const defaultVariant = (product) => {
    if (!product.variants?.length) return null;
    return product.variants.find((v) => v.id === product.default_variant_id) || product.variants[0];
  };

  return (
    <RequireShop>
      <PageHeader
        title="Products"
        description="Manage your catalog, pricing, and publish status."
        actions={
          <>
            <Link to="/shop/categories" className="btn btn-secondary">
              Categories
            </Link>
            <Link to="/shop/collections" className="btn btn-secondary">
              Collections
            </Link>
            <Link to="/shop/products/new" className="btn btn-primary">
              Add product
            </Link>
          </>
        }
      />

      <ErrorBanner message={error} />

      {loading ? (
        <LoadingState />
      ) : products.length === 0 ? (
        <EmptyState message="No products yet. Create a category first, then add your first product." />
      ) : (
        <SectionCard title="Catalog">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Price</th>
                <th>Variants</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const variant = defaultVariant(product);
                return (
                  <tr key={product.id}>
                    <td>
                      <strong>{product.name}</strong>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{product.slug}</div>
                    </td>
                    <td>{variant?.sku || '—'}</td>
                    <td>
                      {variant
                        ? formatMoney(variant.price, variant.currency)
                        : '—'}
                    </td>
                    <td>{product.variants?.length || 0}</td>
                    <td>
                      <span className={`badge ${product.is_live ? 'badge-success' : 'badge-muted'}`}>
                        {product.is_live ? 'Live' : product.status}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/shop/products/${product.id}`)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SectionCard>
      )}
    </RequireShop>
  );
}
